/**
 * @quotech/ui/kanban — board theming (PR 1 of the Trello-densify
 * sequence after the qf!2436 visual restyle).
 *
 * Each theme drives FOUR colors that the board view exposes as CSS
 * custom properties on its outermost div:
 *
 *   --board-bg     canvas background behind the swim lanes
 *   --lane-bg      lane shell background (slightly darker than canvas)
 *   --card-bg      card background (slightly darker again, for contrast)
 *   --accent       accent for hover ring + selected/sticky bits
 *
 * Resolution order in `getBoardTheme(board)`:
 *   1. Operator override at `board.config_json.theme_preset` →
 *      look up that named preset.
 *   2. Operator override at `board.config_json.bg_color` →
 *      synthesise a LIGHT theme from that single hex (soft hue wash
 *      canvas, paler lane, white card; the hue survives as the accent).
 *      Lets the "free pick a hex" affordance keep working without the
 *      operator having to also set `theme_preset`.
 *   3. JOB boards: deterministic hash(board.id) % palette so every
 *      site visibly differs at a glance — matches the operator's
 *      reference screenshots ("353 View Street" was teal, "C384a
 *      Hartley, Clareville" was emerald).
 *   4. Otherwise: `null` (use the default neutral zinc shell).
 *
 * LIGHT-ONLY (FIX-2026-06-15-KANBAN-LIGHT-BOARD, reconciled): the eight
 * BOARD_THEMES are authored as curated LIGHT palettes (soft pastel
 * canvas, paler lane, white card, VIVID per-board accent). Operator
 * directive: "Always use default light colors on kanban background. Dark
 * colors aren't good for visibility." So `getBoardTheme(board)` ships the
 * SAME light surfaces regardless of the app's light/dark mode — the board
 * never renders a dark island (the original "why is there still dark
 * backgrounds on kanban" complaint) nor a dark surface in dark mode that
 * the operator called illegible. Per-board identity is carried entirely
 * by the saturated `accent` (left card stripe, hover ring, today-line),
 * and `dark:false` makes `readableForeground()` resolve dark, WCAG-legible
 * text on the light surfaces. (An earlier draft made theming mode-aware
 * via a `mode` param + `toLightTheme`; that was removed when it collided
 * with the operator's always-light directive — kept here as history so a
 * future agent doesn't re-add dark-mode-dark-boards without re-checking.)
 */

import type { Board } from './types'
import { readableForeground } from './utils'

export interface BoardTheme {
  preset: string
  /** CSS color for the board canvas (behind the lanes). */
  bg: string
  /** CSS color for the lane shell. Slightly darker than `bg`. */
  lane_bg: string
  /** CSS color for cards. Slightly darker than `lane_bg`. */
  card_bg: string
  /** Accent — hover rings, today-line on Gantt etc. */
  accent: string
  /** Dark theme indicator — switches text contrast to light when true. */
  dark: boolean
  /** Optional background image URL. When set, the board canvas
   *  layers a `cover/center` image behind the lanes; the `bg`
   *  colour shows through any letterboxing.
   *  Added 2026-05-04 (board background-images MR). */
  bg_image_url?: string
}

// Curated background images — Trello-style "scenic photo" picks.
// All are Unsplash CDN URLs sized to ~1920px wide so the asset is
// crisp on a 4K monitor without breaking on dial-up. Operators can
// also paste their own URL via the picker's "Custom URL" field.
export const BOARD_BG_IMAGES = [
  { id: 'mountain', label: 'Mountain', url: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&w=1920&q=70', accent_theme: 'slate' },
  { id: 'ocean',    label: 'Ocean',    url: 'https://images.unsplash.com/photo-1505144808419-1957a94ca61e?auto=format&w=1920&q=70', accent_theme: 'sky' },
  { id: 'forest',   label: 'Forest',   url: 'https://images.unsplash.com/photo-1448375240586-882707db888b?auto=format&w=1920&q=70', accent_theme: 'emerald' },
  { id: 'desert',   label: 'Desert',   url: 'https://images.unsplash.com/photo-1473580044384-7ba9967e16a0?auto=format&w=1920&q=70', accent_theme: 'amber' },
  { id: 'aurora',   label: 'Aurora',   url: 'https://images.unsplash.com/photo-1483347756197-71ef80e95f73?auto=format&w=1920&q=70', accent_theme: 'violet' },
  { id: 'plain',    label: 'Solid',    url: '', accent_theme: '' },
] as const

// Eight presets. The canvas (`bg`) carries a SOFT hue wash — that's how
// the operator distinguishes one site from another at a glance — while
// the lane and card surfaces stay near-white so text and cards read
// cleanly. Stacking (Trello's rule): canvas (most tinted) -> lane
// (lighter) -> card (white, floats highest).
//
// LIGHT-FIRST (2026-06-15) — operator: "Always use default light colors
// on kanban board background. Dark colors aren't good for visibility."
// The previous palettes were all `dark:true` deep hues; on Newmark's
// 130+ TRELLO_MIRROR boards (which auto-theme by hash) that meant nearly
// every board rendered dark, which the operator found hard to read.
//
// CALM-SURFACE RE-TUNE (Stage 4 visual lift, 2026-06-20) — the prior
// hand-picked tints varied in strength hue-to-hue (pink/amber read paler
// than teal/indigo), so a wall of auto-themed boards felt patchy. Every
// preset's surfaces are now generated from ONE recipe off its accent:
//   • bg (canvas)  — accent mixed 86% toward white (~14% hue): a soft,
//                    uniform wash. Light enough to keep dark text legible,
//                    tinted enough to tell boards apart at a glance.
//   • lane_bg      — accent mixed 93% toward white (~7% hue): an even
//                    paler whisper so lanes sit just above the canvas.
//   • card_bg      — pure white so cards clearly float on top.
//   • accent       — the VIVID hue, UNCHANGED; per-board identity (card
//                    accent border-left, hover ring, today-line) is stable
//                    so operators still recognise a board by its colour.
// Recipe guarantees the theme.test.ts pins by construction: bg & lane_bg
// luminance stay > 0.55 (so readableForeground → dark #1f2937 text) and
// bg is more tinted than lane_bg (luminance(bg) < luminance(lane_bg)).
// All keep dark:false. To re-tune, change the accent and regenerate the
// two surface hexes with mixHex(accent,'#ffffff',{0.86,0.93}).
export const BOARD_THEMES: BoardTheme[] = [
  { preset: 'teal',    bg: '#ddf0ee', lane_bg: '#eef8f7', card_bg: '#ffffff', accent: '#0d9488', dark: false },
  { preset: 'emerald', bg: '#dcf0ea', lane_bg: '#eef8f4', card_bg: '#ffffff', accent: '#059669', dark: false },
  { preset: 'indigo',  bg: '#e6e5fb', lane_bg: '#f3f2fd', card_bg: '#ffffff', accent: '#4f46e5', dark: false },
  { preset: 'violet',  bg: '#ede3fc', lane_bg: '#f6f1fe', card_bg: '#ffffff', accent: '#7c3aed', dark: false },
  { preset: 'amber',   bg: '#faecdc', lane_bg: '#fcf5ee', card_bg: '#ffffff', accent: '#d97706', dark: false },
  { preset: 'pink',    bg: '#fae1ec', lane_bg: '#fcf0f5', card_bg: '#ffffff', accent: '#db2777', dark: false },
  { preset: 'slate',   bg: '#e0e9fc', lane_bg: '#f0f4fe', card_bg: '#ffffff', accent: '#2563eb', dark: false },
  { preset: 'sky',     bg: '#dceef7', lane_bg: '#edf6fb', card_bg: '#ffffff', accent: '#0284c7', dark: false },
]

/** Stable 32-bit hash of a string. Used to map a board.id → palette
 *  index without needing a backend round-trip. */
function hashString(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h) + s.charCodeAt(i)
    h |= 0
  }
  return Math.abs(h)
}

/** Synthesise a BoardTheme from a single hex. Used when the operator
 *  picks a free-form `bg_color` via the palette popover.
 *
 *  LIGHT-FIRST (2026-06-15) — operator: "Always use default light colors
 *  on kanban background. Dark colors aren't good for visibility." We no
 *  longer render the raw pick as a dark canvas. Instead the picked hue
 *  becomes a SOFT WASH (mixed heavily toward white) for the canvas, with
 *  an even paler lane and pure-white cards. The vivid pick survives only
 *  as the `accent` (left card stripe + hover ring) so the operator still
 *  gets a recognisable colour identity without a dark, low-legibility
 *  surface. `dark:false` -> readableForeground resolves dark text. */
function themeFromHex(hex: string): BoardTheme {
  return {
    preset: 'custom',
    bg: mixHex(hex, '#ffffff', 0.8),
    lane_bg: mixHex(hex, '#ffffff', 0.9),
    card_bg: '#ffffff',
    // Darken very pale picks a touch so the stripe/ring stays visible
    // against a white card; leave saturated picks as-is.
    accent: shadeHex(hex, -0.1),
    dark: false,
  }
}

/** Linearly mix `from` toward `to` by `t` (0 = pure `from`, 1 = pure
 *  `to`). Used to produce subtly-tinted neutral-dark surfaces from a
 *  saturated canvas hex without an HSL detour. Returns the original
 *  `from` on parse failure so the rest of the theme still resolves. */
function mixHex(from: string, to: string, t: number): string {
  const a = parseHex(from)
  const b = parseHex(to)
  if (!a || !b) return from
  const clampT = Math.min(1, Math.max(0, t))
  const r = Math.round(a.r + (b.r - a.r) * clampT)
  const g = Math.round(a.g + (b.g - a.g) * clampT)
  const bl = Math.round(a.b + (b.b - a.b) * clampT)
  return '#' + ((1 << 24) | (r << 16) | (g << 8) | bl).toString(16).slice(1)
}

/** Convert a hex colour into an `rgba(...)` string with the given alpha,
 *  so card secondary text can use the readable foreground at reduced
 *  intensity without losing legibility. Returns the original hex on
 *  parse failure (alpha just won't apply, but no crash). */
function _withAlpha(hex: string, alpha: number): string {
  const p = parseHex(hex)
  if (!p) return hex
  const a = Math.min(1, Math.max(0, alpha))
  return `rgba(${p.r}, ${p.g}, ${p.b}, ${a})`
}

function parseHex(hex: string): { r: number; g: number; b: number } | null {
  let h = hex.trim().replace('#', '')
  if (h.length === 3) h = h.split('').map((c) => c + c).join('')
  if (h.length !== 6) return null
  const num = parseInt(h, 16)
  if (Number.isNaN(num)) return null
  return { r: (num >> 16) & 0xff, g: (num >> 8) & 0xff, b: num & 0xff }
}

/** Shift a hex color by a percentage toward black (negative) or white
 *  (positive). Handles the 3-char `#fff` shorthand and the 6-char
 *  `#ffffff` form. Returns the original input on parse failure. */
function shadeHex(hex: string, pct: number): string {
  let h = hex.trim().replace('#', '')
  if (h.length === 3) {
    h = h.split('').map((c) => c + c).join('')
  }
  if (h.length !== 6) return hex
  const num = parseInt(h, 16)
  if (Number.isNaN(num)) return hex
  let r = (num >> 16) & 0xff
  let g = (num >> 8) & 0xff
  let b = num & 0xff
  if (pct < 0) {
    const f = 1 + pct
    r = Math.max(0, Math.round(r * f))
    g = Math.max(0, Math.round(g * f))
    b = Math.max(0, Math.round(b * f))
  } else {
    r = Math.min(255, Math.round(r + (255 - r) * pct))
    g = Math.min(255, Math.round(g + (255 - g) * pct))
    b = Math.min(255, Math.round(b + (255 - b) * pct))
  }
  return '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)
}

/** Resolve a Board → BoardTheme | null. See file docstring for the
 *  resolution order. Returns `null` when no theme should be applied
 *  (operator hasn't set one + board kind isn't auto-themed). */
export function getBoardTheme(
  board: Board | null | undefined,
): BoardTheme | null {
  if (!board) return null
  const cfg = (board.config_json ?? {}) as {
    theme_preset?: string
    bg_color?: string
    bg_image_url?: string
    bg_image_id?: string
  }
  let base: BoardTheme | null = null
  // 1. named preset
  if (cfg.theme_preset) {
    const named = BOARD_THEMES.find((t) => t.preset === cfg.theme_preset)
    if (named) base = named
  }
  // 2. legacy free-form hex
  if (!base && cfg.bg_color && /^#?[0-9a-fA-F]{3,6}$/.test(cfg.bg_color)) {
    base = themeFromHex(cfg.bg_color.startsWith('#') ? cfg.bg_color : `#${cfg.bg_color}`)
  }
  // 3. JOB boards get an auto-coloured theme so the operator can tell
  //    one site from another at a glance. The hash is stable per
  //    board.id so the colour doesn't shift between page loads.
  //    TRELLO_MIRROR boards opt into the same auto-theming for the
  //    same reason — Newmark has 130+ Trello mirrors and a
  //    deterministic per-board colour makes them visually scannable
  //    in the gallery.
  if (!base && (board.kind === 'JOB' || board.kind === 'TRELLO_MIRROR')) {
    const idx = hashString(board.id) % BOARD_THEMES.length
    base = BOARD_THEMES[idx]
  }

  // Background-image overlay. Even when no colour theme is set, an
  // image-only board still needs a base palette for the lane/card
  // shells, so we fall back to the slate preset (deepest neutral)
  // when only the image was picked.
  let imageUrl: string | undefined
  if (cfg.bg_image_url) {
    imageUrl = cfg.bg_image_url
  } else if (cfg.bg_image_id) {
    imageUrl = BOARD_BG_IMAGES.find((b) => b.id === cfg.bg_image_id)?.url || undefined
  }
  if (imageUrl) {
    if (!base) base = BOARD_THEMES.find((t) => t.preset === 'slate') ?? BOARD_THEMES[0]
    base = { ...base, bg_image_url: imageUrl }
  }

  if (!base) return null
  // LIGHT-ONLY: BOARD_THEMES (and themeFromHex) are authored as curated
  // light palettes, so the resolved `base` is shipped as-is in every app
  // mode. Per-board identity rides on `accent`; `dark:false` lets
  // readableForeground() resolve dark, legible text on the light surfaces.
  // (No light/dark branch: operator directive is "always light".)
  return base
}

/** Build the inline `style` object the BoardView spreads onto its
 *  canvas wrapper. Returns an empty object when `theme` is null.
 *  When a `bg_image_url` is set, also emits the canvas
 *  `backgroundImage` + `backgroundSize: cover` properties so the
 *  image renders behind the lanes. */
export function themeCssVars(theme: BoardTheme | null): Record<string, string> {
  if (!theme) return {}
  // Compute a foreground colour for the card surface using WCAG luminance.
  // Bug 2026-05-15 — themed boards had `--card-bg` set to a saturated /
  // dark muted hex (e.g. #352c4c for the violet preset, or anything in
  // the JOB hash range) but card text was hard-coded to
  // `text-neutral-900 dark:text-neutral-100`. That key follows the global
  // Tailwind `.dark` class, NOT the board theme. On a light Tailwind page
  // viewing a dark-themed board → dark text on dark card → unreadable.
  // Exposing `--card-fg` here lets KanbanCard pick its title/summary colour
  // from the actual card_bg luminance, independent of the global theme.
  // We also emit a muted variant (~70% opacity) for secondary text like
  // dates / chip labels so the hierarchy survives the foreground swap.
  const cardFg = readableForeground(theme.card_bg)
  const laneFg = readableForeground(theme.lane_bg)
  const cardFgMuted = _withAlpha(cardFg, 0.7)
  const laneFgMuted = _withAlpha(laneFg, 0.7)
  const out: Record<string, string> = {
    ['--board-bg' as string]: theme.bg,
    ['--lane-bg' as string]: theme.lane_bg,
    ['--card-bg' as string]: theme.card_bg,
    ['--board-accent' as string]: theme.accent,
    ['--card-fg' as string]: cardFg,
    ['--card-fg-muted' as string]: cardFgMuted,
    ['--lane-fg' as string]: laneFg,
    ['--lane-fg-muted' as string]: laneFgMuted,
  }
  if (theme.bg_image_url) {
    // Scrim follows the theme's mode: a dark gradient keeps light text
    // legible over a photo on dark boards; a light gradient does the
    // same for the dark text used on light-mode boards.
    const scrim = theme.dark
      ? 'linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.55))'
      : 'linear-gradient(rgba(255,255,255,0.55), rgba(255,255,255,0.65))'
    out.backgroundImage = `${scrim}, url("${theme.bg_image_url}")`
    out.backgroundSize = 'cover'
    out.backgroundPosition = 'center'
    out.backgroundRepeat = 'no-repeat'
    out.backgroundAttachment = 'local'
  }
  return out
}
